import React, { useEffect, useState } from 'react';
import { getParties, createParty, updateParty } from '../lib/api';
import { Users, Plus, Edit2, Search, X } from 'lucide-react';

export default function PartiesPage() {
  const [parties, setParties] = useState([]);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ party_name: '', pan_number: '', gstin: '', mobile_number: '', state: '' });

  const load = () => {
    setLoading(true);
    getParties({ search }).then(r => { setParties(r.data.parties || []); setTotal(r.data.total || 0); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) { await updateParty(editItem._id, form); } else { await createParty(form); }
      setShowForm(false); setEditItem(null); setForm({ party_name: '', pan_number: '', gstin: '', mobile_number: '', state: '' }); load();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const startEdit = (p) => {
    setEditItem(p);
    setForm({ party_name: p.party_name, pan_number: p.pan_number || '', gstin: p.gstin || '', mobile_number: p.mobile_number || '', state: p.state || '' });
    setShowForm(true);
  };

  return (
    <div className="space-y-4" data-testid="parties-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-boss-text">Party Master</h1><p className="text-boss-text-muted text-sm">{total} parties</p></div>
        <button onClick={() => { setShowForm(true); setEditItem(null); setForm({ party_name: '', pan_number: '', gstin: '', mobile_number: '', state: '' }); }} className="flex items-center gap-2 px-4 py-2 bg-boss-violet text-white rounded-lg text-sm font-medium hover:bg-boss-violet-2" data-testid="add-party-btn"><Plus size={16} /> Add Party</button>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-boss-text-muted" />
        <input type="text" placeholder="Search parties..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" data-testid="party-search" />
      </div>

      {showForm && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-boss-text">{editItem ? 'Edit Party' : 'Add Party'}</h3>
            <button onClick={() => { setShowForm(false); setEditItem(null); }}><X size={18} /></button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input required placeholder="Party Name *" value={form.party_name} onChange={e => setForm(f => ({...f, party_name: e.target.value}))} className="px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" data-testid="party-name-input" />
            <input placeholder="PAN Number" value={form.pan_number} onChange={e => setForm(f => ({...f, pan_number: e.target.value}))} className="px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" />
            <input placeholder="GSTIN" value={form.gstin} onChange={e => setForm(f => ({...f, gstin: e.target.value}))} className="px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" />
            <input placeholder="Mobile Number" value={form.mobile_number} onChange={e => setForm(f => ({...f, mobile_number: e.target.value}))} className="px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" />
            <input placeholder="State" value={form.state} onChange={e => setForm(f => ({...f, state: e.target.value}))} className="px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" />
            <div className="flex gap-2"><button type="submit" className="px-6 py-2 bg-boss-violet text-white rounded-lg text-sm font-medium hover:bg-boss-violet-2" data-testid="party-submit-btn">{editItem ? 'Update' : 'Create'}</button></div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-boss-border overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-boss-border bg-boss-surface-2">
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Party Name</th>
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">PAN</th>
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">GSTIN</th>
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Mobile</th>
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">State</th>
            <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Action</th>
          </tr></thead>
          <tbody>
            {loading ? [...Array(5)].map((_, i) => <tr key={i}><td colSpan={6} className="py-3 px-4"><div className="h-4 bg-boss-surface-3 rounded animate-pulse" /></td></tr>) : parties.length === 0 ? (
              <tr><td colSpan={6} className="py-12 text-center text-boss-text-muted">No parties found</td></tr>
            ) : parties.map(p => (
              <tr key={p._id} className="border-b border-boss-border/50 hover:bg-boss-surface-2/50" data-testid={`party-row-${p._id}`}>
                <td className="py-2.5 px-4 font-medium">{p.party_name}</td>
                <td className="py-2.5 px-4 font-mono text-xs">{p.pan_number || '---'}</td>
                <td className="py-2.5 px-4 font-mono text-xs">{p.gstin || '---'}</td>
                <td className="py-2.5 px-4 font-mono text-xs">{p.mobile_number || '---'}</td>
                <td className="py-2.5 px-4">{p.state || '---'}</td>
                <td className="py-2.5 px-4 text-center"><button onClick={() => startEdit(p)} className="text-boss-violet hover:underline text-xs"><Edit2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
